import { getColumnSearchProps, getColumnSortProps } from "@common/utils/table";

/**
 * Generate bank statement table columns configuration
 * @param {Array} categories - Array of category objects
 * @param {Object} sortedInfo - Sorting information state
 * @param {Function} handleSearch - Search handler function
 * @param {Function} handleReset - Reset handler function
 * @returns {Array} Column configuration array
 */
export const getBankStatementColumns = (
  categories,
  sortedInfo,
  handleSearch,
  handleReset,
) => {
  return [
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      fixed: "left",
      width: 180,
      filters: categories
        .sort((a, b) => a.title && b.title && a.title.localeCompare(b.title))
        .map((cat) => ({
          key: cat.id,
          text: cat.title.toUpperCase(),
          value: cat.title.toUpperCase(),
        })),
      onFilter: (value, record) =>
        record.category.toUpperCase().indexOf(value) === 0,
      ...getColumnSortProps({
        index: "category",
        type: "text",
        sortedInfo,
      }),
    },
    {
      title: "Entity Key",
      dataIndex: "entity",
      key: "entity",
      fixed: "left",
      width: 180,
      ...getColumnSortProps({
        index: "entity",
        type: "text",
        sortedInfo,
      }),
      ...getColumnSearchProps({
        index: "entity",
        handleSearch,
        handleReset,
      }),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      ...getColumnSortProps({
        index: "remarks",
        type: "text",
        sortedInfo,
      }),
      ...getColumnSearchProps({
        index: "remarks",
        handleSearch,
        handleReset,
      }),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      ...getColumnSortProps({
        index: "amount",
        type: "number",
        sortedInfo,
      }),
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      ...getColumnSortProps({
        index: "remarks",
        type: "text",
        sortedInfo,
      }),
      ...getColumnSearchProps({
        index: "remarks",
        handleSearch,
        handleReset,
      }),
    },
    {
      title: "Center",
      dataIndex: "cong",
      key: "cong",
      ...getColumnSortProps({
        index: "cong",
        type: "text",
        sortedInfo,
      }),
      ...getColumnSearchProps({
        index: "cong",
        handleSearch,
        handleReset,
      }),
    },
    {
      title: "Service",
      dataIndex: "region",
      key: "region",
      ...getColumnSortProps({
        index: "region",
        type: "text",
        sortedInfo,
      }),
      ...getColumnSearchProps({
        index: "region",
        handleSearch,
        handleReset,
      }),
    },
    {
      title: "Transaction Description 2",
      dataIndex: "description2",
      key: "description2",
      ...getColumnSearchProps({
        index: "description2",
        handleSearch,
        handleReset,
      }),
    },
  ];
};

/**
 * Generate Tithely table columns configuration
 * @param {Array} categories - Array of category objects
 * @param {Object} sortedInfo - Sorting information state
 * @param {Function} handleSearch - Search handler function
 * @param {Function} handleReset - Reset handler function
 * @returns {Array} Column configuration array
 */
export const getTithelyColumns = (
  categories,
  sortedInfo,
  handleSearch,
  handleReset,
) => {
  return [
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      fixed: "left",
      width: 180,
      filters: categories
        .sort((a, b) => a.title && b.title && a.title.localeCompare(b.title))
        .map((cat) => ({
          key: cat.id,
          text: cat.title.toUpperCase(),
          value: cat.title.toUpperCase(),
        })),
      onFilter: (value, record) =>
        record.category.toUpperCase().indexOf(value) === 0,
      ...getColumnSortProps({
        index: "category",
        type: "text",
        sortedInfo,
      }),
    },
    {
      title: "EntityKey",
      dataIndex: "entity",
      key: "entity",
      fixed: "left",
      width: 180,
      ...getColumnSortProps({
        index: "entity",
        type: "text",
        sortedInfo,
      }),
      ...getColumnSearchProps({
        index: "entity",
        handleSearch,
        handleReset,
      }),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      ...getColumnSortProps({
        index: "date",
        type: "date",
        sortedInfo,
      }),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      ...getColumnSearchProps({
        index: "remarks",
        handleSearch,
        handleReset,
      }),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Center",
      dataIndex: "cong",
      key: "cong",
      ...getColumnSearchProps({
        index: "cong",
        handleSearch,
        handleReset,
      }),
    },
    {
      title: "Service",
      dataIndex: "region",
      key: "region",
      ...getColumnSearchProps({
        index: "region",
        handleSearch,
        handleReset,
      }),
    },
    {
      title: "Giving Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Check / iBank / NETS Number",
      dataIndex: "checkIbankNets",
      key: "checkIbankNets",
    },
    {
      title: "Memo / Note",
      dataIndex: "memo",
      key: "memo",
    },
    {
      title: "Backdate",
      dataIndex: "backdate",
      key: "backdate",
    },
  ];
};

/**
 * Generate summary table columns configuration
 * @param {Array} categories - Array of category objects
 * @param {Object} sortedInfo - Sorting information state
 * @param {Function} handleSearch - Search handler function
 * @param {Function} handleReset - Reset handler function
 * @returns {Array} Column configuration array
 */
export const getSummaryColumns = (
  categories,
  sortedInfo,
  handleSearch,
  handleReset,
) => {
  return [
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      fixed: "left",
      width: 180,
      filters: categories
        .sort((a, b) => a.title && b.title && a.title.localeCompare(b.title))
        .map((cat) => ({
          key: cat.id,
          text: cat.title.toUpperCase(),
          value: cat.title.toUpperCase(),
        })),
      onFilter: (value, record) =>
        record.category.toUpperCase().indexOf(value) === 0,
      ...getColumnSortProps({
        index: "category",
        type: "text",
        sortedInfo,
      }),
    },
    {
      title: "Cong",
      dataIndex: "cong",
      key: "cong",
      ...getColumnSearchProps({
        index: "cong",
        handleSearch,
        handleReset,
      }),
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      ...getColumnSearchProps({
        index: "region",
        handleSearch,
        handleReset,
      }),
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
    },
  ];
};
